/* eslint-disable no-restricted-syntax */
/* eslint-disable import/no-cycle */
/**
 * Copyright (c) 2021, Ouster, Inc.
 * All rights reserved.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components/macro';

import { DropdownSelector } from './DropdownSelector';
import { TagControls } from './TagControls';
import { useStoreState } from '../reducers';
import {
  DropdownData,
  ArchitectSceneTableColumn,
  ArchitectFilterState,
  ArchitectFilterableField,
  ArchitectFilterSectionInfo,
} from '../types';
import {
  setArchitectSceneFiltersInUrl,
  updateArchitectFiltersInUrl,
  UrlArchitectSceneFilterState,
} from '../utils/searchParams';

/// ///////////////////////////////////////////////////////////////////////////
// Filter Component
/// ///////////////////////////////////////////////////////////////////////////

const FilterContainer = styled.div<{ width: string }>`
  box-shadow: 0px 0px 12px rgba(0, 0, 0, 0.2);
  background-color: var(--color-milky-way);
  border-radius: 4px;

  z-index: var(--z-overlay);

  width: ${(props) => props.width};
  max-height: 404px;
  padding-top: 16px;

  /* Hide by default, will be revealed when parent focuses */
  display: none;

  /* Position below the header and its border but out of DOM flow */
  position: absolute;
  top: calc(100% + 2px); /* headers border is 2px */

  /* Make filters a bit bigger as set in designs */
  font-size: 14px;
`;

// Get all filter values (string) based on filterState. Useful to
// display all selected filter values, regarless of which filter section
// they come from.
const allValuesFromFilters = (filters?: ArchitectFilterState) => {
  if (filters === undefined) return [];

  return filters.reduce(
    (acc, vals) => [...acc, ...vals.values.map((data) => data.name)],
    [] as string[]
  );
};
// Variant of function above, useful when dealing with tags
const allValuesFromOptions = (
  opts: Map<ArchitectFilterSectionInfo, DropdownData[]>
) => {
  let rs: string[] = [];
  for (const dataArr of opts.values()) {
    rs = [...rs, ...dataArr.map((d) => d.name)];
  }
  return rs;
};

// Return part of filterState corresponding to a set of fields
const filterStateForFields = (
  filterState: ArchitectFilterState,
  fields: ArchitectFilterableField[]
) => {
  // Keep ordering of fields instead of ordering of filterState. To do
  // so, we here use a tmp mapping between a field and its section index
  // in filterState.
  const filterSectionIdxByField: Map<ArchitectFilterableField, number> =
    new Map();
  filterState.forEach((state, sectionIdx) => {
    filterSectionIdxByField.set(state.sectionInfo.filterField, sectionIdx);
  });
  return (
    fields
      .map((field) => filterSectionIdxByField.get(field))
      // Narrow sectionIdx type after filtering
      .filter((sectionIdx): sectionIdx is number => sectionIdx !== undefined)
      .map((sectionIdx) => filterState[sectionIdx])
  );
};

// Based on selected string values, reconstruct the filter state by
// finding which section each string came from
// TODO(pierre) on 2021-05-11: This isn't great. Maybe we should also
// have the DropdownList surface the selection breakdown by section
// instead of just the selected strings
const columnFilterState = (
  selectedValues: string[],
  columnOptions: Map<ArchitectFilterSectionInfo, DropdownData[]>
) => {
  // Store temporary results into a Map for easier object access while
  // we reconstruct the filter state
  const valuesPerSection: Map<ArchitectFilterSectionInfo, DropdownData[]> =
    new Map();

  // Initialize as empty filters
  for (const section of columnOptions.keys()) {
    valuesPerSection.set(section, []);
  }

  selectedValues.forEach((val) => {
    // Look for possible filter sections containing this string
    const possibleSections: ArchitectFilterSectionInfo[] = [];
    for (const [section, dataArr] of columnOptions.entries()) {
      if (dataArr.map((data) => data.name).includes(val)) {
        possibleSections.push(section);
      }
    }

    if (possibleSections.length === 0) {
      console.warn(`No options corresponding to filter value ${val}`);
    } else {
      if (possibleSections.length > 1) {
        console.warn(
          `Multiple options for filter value ${val} : ${possibleSections}. Will pick the first one.`
        );
      }
      const section = possibleSections[0];
      if (valuesPerSection.get(section)) {
        valuesPerSection.get(section)?.push({ name: val });
      } else {
        valuesPerSection.set(section, [{ name: val }]);
      }
    }
  });

  // Convert our Map into a FilterState
  const colFilters: ArchitectFilterState = [];
  for (const [section, dataArr] of valuesPerSection.entries()) {
    colFilters.push({ sectionInfo: section, values: dataArr });
  }
  return colFilters;
};

const ArchitectSceneFilter: React.FC<{
  column: ArchitectSceneTableColumn;
  width: string;
  fields: ArchitectFilterableField[];
  setInputHandle?: (input: HTMLInputElement) => void;
}> = ({ column, width, fields, setInputHandle }) => {
  // State for this filter (i.e. selected values organized by section)
  const [filterState, setFilterState] = useState<ArchitectFilterState>([]);
  // Options the user can select between to filter drives. Kept as a Map
  // to play nicely with DropdownSelector types
  const [options, setOptions] = useState<
    Map<ArchitectFilterSectionInfo, DropdownData[]>
  >(new Map());

  const { architectFilterOptions, activeFilters } = useStoreState(
    (storeState) => ({
      ...storeState.architectSceneList,
    })
  );

  // Set filter options for this DriveFilter by only keeping the values
  // for the fields of interest
  const setFilterOptions = useCallback(
    (allOptions: ArchitectFilterState) => {
      const newOptions: Map<ArchitectFilterSectionInfo, DropdownData[]> =
        new Map();
      filterStateForFields(allOptions, fields).forEach((filterSection) => {
        newOptions.set(filterSection.sectionInfo, filterSection.values);
      });
      setOptions(newOptions);
    },
    [fields]
  );

  // If we're dealing with Sdk data, set filter options for this
  // filter based on the fgFilterOptions loaded in reducer
  useEffect(() => {
    if (
      architectFilterOptions &&
      Object.keys(architectFilterOptions).length !== 0
    ) {
      setFilterOptions(architectFilterOptions);
    }
  }, [setFilterOptions, architectFilterOptions]);

  // When filters state change in the reducer, update selected filters
  // for this component
  useEffect(() => {
    if (activeFilters !== undefined) {
      setFilterState(filterStateForFields(activeFilters, fields));
    }
  }, [fields, activeFilters]);

  const { curUserOrg } = useStoreState((storeState) => storeState.user);

  return (
    <FilterContainer width={width}>
      {column === ArchitectSceneTableColumn.TAGS ? (
        <TagControls
          selectedTags={allValuesFromFilters(filterState)}
          onChange={(newTags) => {
            updateArchitectFiltersInUrl(columnFilterState(newTags, options));
          }}
          placeholder="Tags to filter by"
          vertical
          setInputHandle={setInputHandle}
          subsetTagsToChooseFrom={allValuesFromOptions(options)}
          height="100px"
          orgId={curUserOrg?.id}
        />
      ) : (
        <DropdownSelector
          options={options}
          onSelectionChange={(dropdownSelection) => {
            updateArchitectFiltersInUrl(
              columnFilterState(dropdownSelection, options)
            );
          }}
          selectedItems={allValuesFromFilters(filterState)}
          placeholder="Type your filter"
          setInputHandle={setInputHandle || (() => {})}
          vertical
          height="100px"
        />
      )}
    </FilterContainer>
  );
};

/// ///////////////////////////////////////////////////////////////////////////
// Reset Button
/// ///////////////////////////////////////////////////////////////////////////
const ResetButton = styled.button<{ hidden: boolean }>`
  /* Reset button css since this is just text */
  all: unset;
  cursor: pointer;

  ${(props) => props.hidden && 'display: none;'}

  position: absolute;
  right: 0;
  font-size: 12px;
  font-weight: 600;
  color: var(--color-blast-off-blue);
  text-transform: uppercase;
  ::before {
    content: 'Reset';
  }
`;

const ResetFiltersButton: React.FC = () => {
  const { activeFilters } = useStoreState((storeState) => ({
    ...storeState.architectSceneList,
  }));

  return (
    <ResetButton
      hidden={allValuesFromFilters(activeFilters).length === 0}
      onClick={() => {
        setArchitectSceneFiltersInUrl(new UrlArchitectSceneFilterState({}));
      }}
    />
  );
};

/// ///////////////////////////////////////////////////////////////////////////
// Exports
/// ///////////////////////////////////////////////////////////////////////////
export { FilterContainer, ArchitectSceneFilter, ResetFiltersButton };
